import { useTranslation } from 'react-i18next';
import { convertDateToTimeZone, DateFormatter } from 'infrastructure/functions';
import { DateFormats, type ReadingStatus } from 'infrastructure/enums';
import useUser from 'utils/useUser';
import { formatAuthorName } from 'utils/formatters';

import s from './styles.module.scss';
import DateCell from './date-cell';
import StatusCell from './status-cell';
import ReadingCell from './reading-cell';
import ThresholdCell from './threshold-cell';
import CommentsCell from './comments-cell';
import LinkedCallsCell from './linked-calls-cell';

import type {
  IActivityComments,
  IActivityData,
  ILinkedReadingCall,
} from 'infrastructure/interfaces/i-rpm-patient-activity';
import type { IBaseTableColumns } from 'components/atoms/base-table/types';

interface IProps {
  page: number;
  items: number;
  name: string;
}
export const useColumns = ({ page, items, name }: IProps) => {
  const { t } = useTranslation();
  const { isCriticalOrAtRiskAlertingRole } = useUser();

  const currentPage = page ?? 1;
  const itemIndexPrefix = (currentPage - 1) * (items ?? 100);
  const columns: IBaseTableColumns<IActivityData> = [
    {
      label: '№',
      key: 'guid',
      width: '50px',
      align: 'center',
      render: (_, record, idx) => (
        <p className={s.number}>#{itemIndexPrefix + idx + 1}</p>
      ),
    },
    {
      label: t('labels.status'),
      key: 'globalStatus',
      align: 'center',
      width: '120px',
      render: (globalStatus: ReadingStatus, record) => (
        <StatusCell
          globalStatus={globalStatus}
          thresholds={record.patient.threshold}
          thresholdName={record.thresholdName}
          mightBeErroneousReason={record.mightBeErroneousReason}
          isRecovered={record.isRecovered}
          receivedTime={record.receivedTime}
          readingTime={record.dateTime}
          timezone={record.patient.timezone}
        />
      ),
    },
    {
      label: t('labels.readingTime'),
      key: 'createdAt',
      width: '105px',
      align: 'center',
      render: (createdAt: string) => {
        const date = new Date(parseInt(createdAt));

        return <DateCell date={date} />;
      },
    },
    {
      label: t('labels.localTime'),
      key: 'createdAt',
      width: '100px',
      align: 'center',
      render: (createdAt: string, record) => {
        const patientTime = convertDateToTimeZone(
          new Date(parseInt(createdAt)),
          record.patient.timezone,
        );
        return <DateCell date={patientTime} />;
      },
    },
    {
      label: t('labels.reading'),
      key: 'createdAt',
      render: (_, record) => {
        return (
          <ReadingCell
            values={record.value}
            statuses={record.statuses}
            readingType={record.readingType}
          />
        );
      },
    },
    {
      label: t('labels.threshold'),
      key: 'createdAt',
      render: (_, record) => (
        <ThresholdCell
          values={record.value}
          readingType={record.readingType}
          thresholds={record.patient.threshold}
          statuses={record.statuses}
          globalStatus={record.globalStatus}
          thresholdName={record.thresholdName}
        />
      ),
    },
  ];

  if (!isCriticalOrAtRiskAlertingRole) {
    columns.push(
      {
        label: t('labels.comments'),
        key: 'comments',
        width: '165px',
        align: 'center',
        render: (comments: IActivityComments[], record, idx) => {
          const createdAt = DateFormatter({
            date: new Date(parseInt(record.createdAt)),
            format: DateFormats['MM-dd-yyyy | hh:mm a'],
          });
          const subtitle = `#${itemIndexPrefix + idx + 1} ${createdAt}`;

          return (
            <CommentsCell
              comments={comments}
              title={name}
              patientGuid={record.patient.guid}
              subtitle={subtitle}
              values={record.value}
              readingType={record.readingType}
              thresholds={record.patient.threshold}
              statuses={record.statuses}
              globalStatus={record.globalStatus}
              thresholdName={record.thresholdName}
              page={page}
            />
          );
        },
      },
      {
        label: t('linkedCallReadings.linkedCalls'),
        key: 'linkedCalls',
        width: '170px',
        align: 'center',
        render: (linkedCalls: ILinkedReadingCall[] = [], record, idx) => {
          const createdAt = DateFormatter({
            date: new Date(parseInt(record.createdAt)),
            format: DateFormats['MM-dd-yyyy | hh:mm a'],
          });
          const subtitle = `#${itemIndexPrefix + idx + 1} ${createdAt}`;

          return (
            <LinkedCallsCell
              linkedCalls={linkedCalls}
              title={name}
              subtitle={subtitle}
              values={record.value}
              readingType={record.readingType}
              thresholds={record.patient.threshold}
              statuses={record.statuses}
              globalStatus={record.globalStatus}
              thresholdName={record.thresholdName}
              page={page}
              patientGuid={record.patient.guid}
              patientName={formatAuthorName(record.patient)}
              patientPhone={record.patient.phone}
              patientTimezone={record.patient.timezone}
            />
          );
        },
      },
    );
  }

  return columns;
};
